'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';
import type { GRN } from './GRNContent';

interface GRNTableProps {
  grns: GRN[];
  onViewDetail: (grn: GRN) => void;
}

export default function GRNTable({ grns, onViewDetail }: GRNTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<GRN | null>(null);
  const [rejectTarget, setRejectTarget] = useState<GRN | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  if (grns.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-14 text-center shadow-card">
        <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="DocumentTextIcon" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-600 text-foreground">No GRNs found</h3>
        <p className="text-xs text-muted-foreground mt-1.5">
          No Goods Receipt Notes match your current filter. Create a new GRN to get started.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="w-8 px-3 py-3" />
                {['GRN No.', 'Vendor', 'Site', 'Delivery Date', 'Items', 'Total Value', 'Created By', 'Status', 'Actions'].map((h) => (
                  <th key={`grn-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grns.map((grn, idx) => (
                <React.Fragment key={grn.id}>
                  <tr
                    onMouseEnter={() => setHoveredRow(grn.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`border-b border-border transition-colors ${idx % 2 === 1 ? 'bg-muted/20' : ''} ${hoveredRow === grn.id ? 'bg-secondary/5' : ''}`}
                  >
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => setExpandedRow(expandedRow === grn.id ? null : grn.id)}
                        className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
                        title="Expand line items"
                      >
                        <Icon name={expandedRow === grn.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} />
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-data text-xs font-600 text-foreground">{grn.grnNumber}</span>
                        {grn.status === 'APPROVED' && (
                          <Icon name="LockClosedIcon" size={11} className="text-muted-foreground" title="Approved — immutable" />
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono-data">{grn.poReference}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-foreground">{grn.vendor}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{grn.site}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{grn.deliveryDate}</td>
                    <td className="px-3 py-3">
                      <span className="text-xs font-500 text-foreground">{grn.lineItems.length} line{grn.lineItems.length !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm font-600 tabular-nums text-foreground">
                        € {grn.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 0 })}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{grn.createdBy}</td>
                    <td className="px-3 py-3">
                      <StatusBadge status={grn.status as Parameters<typeof StatusBadge>[0]['status']} size="sm" />
                    </td>
                    <td className="px-3 py-3">
                      <div className={`flex items-center gap-1 transition-opacity ${hoveredRow === grn.id ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                          title="View detail"
                          onClick={() => onViewDetail(grn)}
                          className="p-1.5 rounded-md hover:bg-secondary/10 text-secondary transition-colors"
                        >
                          <Icon name="EyeIcon" size={14} />
                        </button>
                        {grn.status === 'PENDING_APPROVAL' && (
                          <>
                            <button
                              title="Approve GRN"
                              onClick={() => setApproveTarget(grn)}
                              className="p-1.5 rounded-md hover:bg-teal/10 text-teal transition-colors"
                            >
                              <Icon name="CheckCircleIcon" size={14} />
                            </button>
                            <button
                              title="Reject GRN"
                              onClick={() => setRejectTarget(grn)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-red-500 transition-colors"
                            >
                              <Icon name="XCircleIcon" size={14} />
                            </button>
                          </>
                        )}
                        {grn.status === 'DRAFT' && (
                          <button
                            title="Submit for inspection"
                            onClick={() => toast.success(`${grn.grnNumber} submitted for inspection.`)}
                            className="p-1.5 rounded-md hover:bg-secondary/10 text-secondary transition-colors"
                          >
                            <Icon name="PaperAirplaneIcon" size={14} />
                          </button>
                        )}
                        {grn.status === 'APPROVED' && (
                          <button
                            title="Download GRN PDF"
                            onClick={() => toast.info(`Generating PDF for ${grn.grnNumber}…`)}
                            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
                          >
                            <Icon name="ArrowDownTrayIcon" size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded line items */}
                  {expandedRow === grn.id && (
                    <tr key={`${grn.id}-expanded`} className="border-b border-border">
                      <td colSpan={10} className="px-6 py-4 bg-muted/30">
                        <div className="rounded-lg border border-border overflow-hidden bg-card">
                          <div className="px-4 py-2 bg-muted/50 border-b border-border flex items-center gap-2">
                            <Icon name="ListBulletIcon" size={14} className="text-muted-foreground" />
                            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Line Items — {grn.grnNumber}</span>
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border">
                                {['Item Code', 'Item Name', 'Ordered', 'Received', 'Rejected', 'UOM', 'Unit Cost', 'Line Total', 'Batch / Expiry'].map((h) => (
                                  <th key={`li-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {grn.lineItems.map((li) => (
                                <tr key={li.id} className="hover:bg-muted/30 transition-colors">
                                  <td className="px-3 py-2">
                                    <span className="font-mono-data text-xs text-foreground">{li.itemCode}</span>
                                  </td>
                                  <td className="px-3 py-2 text-xs text-foreground">{li.itemName}</td>
                                  <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{li.orderedQty.toLocaleString('de-DE')}</td>
                                  <td className="px-3 py-2 text-xs tabular-nums text-teal font-500">{li.receivedQty.toLocaleString('de-DE')}</td>
                                  <td className="px-3 py-2 text-xs tabular-nums text-red-600 font-500">{li.rejectedQty > 0 ? li.rejectedQty.toLocaleString('de-DE') : '—'}</td>
                                  <td className="px-3 py-2 text-xs text-muted-foreground">{li.uom}</td>
                                  <td className="px-3 py-2 text-xs tabular-nums text-foreground">€ {li.unitCost.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-xs tabular-nums font-600 text-foreground">€ {li.totalCost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2 text-xs text-muted-foreground">
                                    {li.batchNumber ? (
                                      <span className="font-mono-data">
                                        {li.batchNumber}
                                        {li.expiryDate && <span className="ml-2 text-amber-600">Exp: {li.expiryDate}</span>}
                                      </span>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {grn.inspectionNotes && (
                          <div className="mt-3 flex gap-2 text-xs text-muted-foreground">
                            <Icon name="ClipboardDocumentCheckIcon" size={13} className="shrink-0 mt-0.5" />
                            <span><span className="font-600 text-foreground">Inspection notes:</span> {grn.inspectionNotes}</span>
                          </div>
                        )}
                        {grn.rejectionReason && (
                          <div className="mt-3 flex gap-2 text-xs text-red-600">
                            <Icon name="ExclamationCircleIcon" size={13} className="shrink-0 mt-0.5" />
                            <span><span className="font-600">Rejection reason:</span> {grn.rejectionReason}</span>
                          </div>
                        )}
                        {grn.approvedBy && (
                          <div className="mt-3 flex gap-2 text-xs text-teal">
                            <Icon name="CheckCircleIcon" size={13} className="shrink-0 mt-0.5" />
                            <span>Approved by <span className="font-600">{grn.approvedBy}</span> on {grn.approvedDate}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground">{grns.length} GRN{grns.length !== 1 ? 's' : ''} shown</span>
        </div>
      </div>

      {/* Approve confirm */}
      <ConfirmModal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => {
          toast.success(`${approveTarget?.grnNumber} approved. Stock updated and weighted average cost recalculated.`);
          setApproveTarget(null);
        }}
        title="Approve GRN"
        message={`Approve ${approveTarget?.grnNumber} from ${approveTarget?.vendor}? This will update stock on-hand and recalculate weighted average costs. This action is irreversible — reversal only via RET-OUT.`}
        confirmLabel="Approve GRN"
        variant="warning"
      />

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-primary/40 backdrop-blur-sm" onClick={() => setRejectTarget(null)} />
          <div className="relative bg-card rounded-xl shadow-modal w-full max-w-md fade-in p-6">
            <h3 className="text-base font-600 text-foreground mb-1">Reject GRN</h3>
            <p className="text-xs text-muted-foreground mb-4">Rejecting {rejectTarget.grnNumber} from {rejectTarget.vendor}</p>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">This reason will be recorded in the audit log and visible to the creator.</p>
              <textarea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Delivery challan mismatch — quantities do not correspond to PO-2026-0305…"
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-input text-foreground outline-none focus:ring-2 focus:ring-ring/30 resize-none transition-all"
              />
            </div>
            <div className="flex gap-3 mt-4 justify-end">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                disabled={!rejectReason.trim()}
                onClick={() => {
                  toast.error(`${rejectTarget.grnNumber} rejected. Vendor notified.`);
                  setRejectTarget(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg font-500 hover:bg-red-700 transition-all disabled:opacity-50"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}